import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle } from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { usePatientDetail } from '@/features/patients/hooks/use-patient-detail'
import { PatientHeader } from '@/features/patients/components/PatientHeader'
import { PatientOverviewTab } from '@/features/patients/components/PatientOverviewTab'
import { PatientReportsTab } from '@/features/patients/components/PatientReportsTab'
import { PatientSummariesTab } from '@/features/patients/components/PatientSummariesTab'
import { PatientInsightsTab } from '@/features/patients/components/PatientInsightsTab'
import { PatientEditModal } from '@/features/patients/components/PatientEditModal'
import { PatientRecordsTab } from '@/features/patients/components/PatientRecordsTab'

export const Route = createFileRoute('/_authenticated/patients/$patientId')({
  component: PatientDetailPage,
})

const tabs = [
  { id: 'overview', label: 'Visão Geral' },
  { id: 'records', label: 'Evolução' },
  { id: 'reports', label: 'Relatos' },
  { id: 'summaries', label: 'Análise Semanal' },
  { id: 'insights', label: 'Alertas' },
]

type PatientEditStep = 'patient' | 'family' | 'scheduling'

function PatientDetailPage() {
  const { patientId } = Route.useParams()
  const [activeTab, setActiveTab] = useState('overview')
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editInitialStep, setEditInitialStep] = useState<PatientEditStep>('patient')

  const { data: patient, isLoading, error, refetch } = usePatientDetail(patientId)

  // Dynamic title with patient name
  useDocumentTitle(patient ? patient.full_name : 'Paciente')

  const openEditModal = (initialStep: PatientEditStep = 'patient') => {
    setEditInitialStep(initialStep)
    setIsEditModalOpen(true)
  }

  if (isLoading) {
    return (
      <MainLayout title="Carregando..." subtitle="Aguarde enquanto carregamos os dados">
        <PatientDetailSkeleton />
      </MainLayout>
    )
  }

  if (error || !patient) {
    return (
      <MainLayout title="Erro" subtitle="Não foi possível carregar o paciente">
        <div className="card p-12 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <p className="text-gray-600 font-medium">
            {error ? 'Erro ao carregar paciente' : 'Paciente não encontrado'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Verifique se o paciente existe e tente novamente
          </p>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout title={patient.full_name} subtitle="Detalhes do paciente">
      <div className="space-y-6">
        {/* Header com info do paciente */}
        <div className="card p-6">
          <PatientHeader
            patient={patient}
            onEdit={() => openEditModal('patient')}
            onDelete={() => {
              // TODO: Confirm and discharge
            }}
          />
        </div>

        {/* Edit Modal */}
        <PatientEditModal
          patient={patient}
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          onSuccess={() => refetch()}
          initialStep={editInitialStep}
        />

        {/* Tabs */}
        <div className="card p-1 flex gap-1 overflow-x-auto scrollbar-none">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? 'bg-teal-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Conteudo das tabs */}
        <div className="card p-6">
          {activeTab === 'overview' && (
            <PatientOverviewTab
              patientId={patientId}
              onEditSchedule={() => openEditModal('scheduling')}
            />
          )}
          {activeTab === 'records' && <PatientRecordsTab patientId={patientId} />}
          {activeTab === 'reports' && <PatientReportsTab patientId={patientId} />}
          {activeTab === 'summaries' && <PatientSummariesTab patientId={patientId} />}
          {activeTab === 'insights' && <PatientInsightsTab patientId={patientId} />}
        </div>
      </div>
    </MainLayout>
  )
}

function PatientDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="card p-6">
        <div className="flex items-start gap-4">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <div className="flex-1">
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
      </div>
      <Skeleton className="h-10 w-96" />
      <div className="card p-6">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    </div>
  )
}
